// Etsy Reviews + recovery tracking API.
//
// USE_REAL_API-gated like the other api/<x>.ts modules: real engine hits the
// live /seller/reviews + /admin/reviews surfaces, maket/dev mode (MSW) the same
// relative paths intercepted by the dev simulation. fetch + apiUrl + throw on
// !res.ok — same shape as api/triage.ts.

import { apiUrl } from '../lib/api'
import { readErrorMessage } from '../lib/api-problem'
import type {
  AdminReviewListParams,
  EtsyReview,
  RecoveryUpdate,
  ReviewListParams,
  ReviewListResponse,
  ReviewSummary,
  ReviewSyncStartResponse,
  TrackingSettingRead,
  TrackingSettingsResponse,
} from './reviews.types'

export type * from './reviews.types'

function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

// ─── Seller (D-6 own org) ───────────────────────────────────────────────────

export async function fetchSellerReviews(
  params: ReviewListParams = {},
): Promise<ReviewListResponse> {
  const res = await fetch(apiUrl(`/seller/reviews${buildQuery(params)}`))
  if (!res.ok) throw new Error(`fetch reviews failed: ${res.status}`)
  return (await res.json()) as ReviewListResponse
}

export async function fetchSellerReviewsSummary(): Promise<ReviewSummary> {
  const res = await fetch(apiUrl('/seller/reviews/summary'))
  if (!res.ok) throw new Error(`fetch reviews summary failed: ${res.status}`)
  return (await res.json()) as ReviewSummary
}

export async function syncSellerReviews(): Promise<ReviewSyncStartResponse> {
  const res = await fetch(apiUrl('/seller/reviews/sync'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as ReviewSyncStartResponse
}

export async function fetchSellerReview(id: string): Promise<EtsyReview> {
  const res = await fetch(apiUrl(`/seller/reviews/${id}`))
  if (!res.ok) throw new Error(`fetch review failed: ${res.status}`)
  return (await res.json()) as EtsyReview
}

export async function startRecovery(reviewId: string): Promise<EtsyReview> {
  const res = await fetch(apiUrl(`/seller/reviews/${reviewId}/recovery`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({}),
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as EtsyReview
}

export async function updateRecovery(
  reviewId: string,
  body: RecoveryUpdate,
): Promise<EtsyReview> {
  const res = await fetch(apiUrl(`/seller/reviews/${reviewId}/recovery`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as EtsyReview
}

// ─── Admin (Ring-3) ─────────────────────────────────────────────────────────

export async function fetchAdminReviews(
  params: AdminReviewListParams = {},
): Promise<ReviewListResponse> {
  const res = await fetch(apiUrl(`/admin/reviews${buildQuery(params)}`))
  if (!res.ok) throw new Error(`fetch admin reviews failed: ${res.status}`)
  return (await res.json()) as ReviewListResponse
}

export async function fetchAdminReviewsSummary(
  params: { type?: 'internal' | 'external'; store?: string } = {},
): Promise<ReviewSummary> {
  const res = await fetch(apiUrl(`/admin/reviews/summary${buildQuery(params)}`))
  if (!res.ok) throw new Error(`fetch admin reviews summary failed: ${res.status}`)
  return (await res.json()) as ReviewSummary
}

export async function fetchTrackingSettings(
  type?: 'internal' | 'external',
): Promise<TrackingSettingsResponse> {
  const res = await fetch(
    apiUrl(`/admin/review-tracking-settings${buildQuery({ type })}`),
  )
  if (!res.ok) throw new Error(`fetch tracking settings failed: ${res.status}`)
  return (await res.json()) as TrackingSettingsResponse
}

export async function toggleAdminTracking(
  storeId: string,
  enabled: boolean,
): Promise<TrackingSettingRead> {
  const res = await fetch(apiUrl(`/admin/review-tracking-settings/${storeId}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as TrackingSettingRead
}

export async function triggerReviewSync(): Promise<ReviewSyncStartResponse> {
  const res = await fetch(apiUrl('/admin/reviews/sync'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as ReviewSyncStartResponse
}
